import { OpenArchiveExportPresetEnum }  from '@app/modules/archiving/models/open-archive-export.model';
import { OpenArchivePresetEnum }        from '@app/modules/archiving/models/open-archive.model';
import { BusinessPremisesPresetEnum }   from '@app/modules/business-premises/models/business-premises.model';
import { ContractPresetEnum }           from '@app/modules/contract/models/contract.model';
import { ErsPresetEnum }                from '@app/modules/ers/models/ers.model';
import { FccVersionPresetEnum }         from '@app/modules/fcc-version/models/fcc-version.model';
import { FccOpenTransactionPresetEnum } from '@app/modules/fcc/fcc-open-transaction/models/fcc-open-transaction.model';
import { FiskalTssLogPresetEnum }       from '@app/modules/fiskal-tss-log/models/fiskal-tss-log.model';
import { KeywordPresetEnum }            from '@app/modules/keyword/models/common-keyword.model';
import { NotificationPresetEnum }       from '@app/modules/notification/models/notification.model';
import { TaxationReportingPresetEnum }  from '@app/modules/taxation/taxation-reporting/models/taxation-reporting.model';
import { TssPresetEnum }                from '@app/modules/tss/models/tss.model';
import { UsagePresetEnum }              from '@app/modules/usage/models/common-usage.model';
import { UserPresetEnum }               from '@app/modules/user/models/user.model';
import { ProductEnum }                  from '@core/interfaces/common.interfaces';
import { IRouteDefinition }             from '@core/interfaces/route.interface';
import {
	EntityActionEventEnum,
	EntityVariantEnum
}                                       from '@models/entity.model';
import { ScopeActionEnum }              from '@models/scope.enums';
import { ManageTenantIdentifierEnum }   from '@shared/content-sections/manage-tenant/manage-tenant.model';
import {
	FormValidatorConfigType,
	FormValidatorIdentifierEnum,
	FormValidatorsConfigMap
}                                       from '@shared/forms/models/form.model';
import { ListOperationsIdentifierEnum } from '@shared/list-operations/model/list-operations.model';

// ENUMS ##############################################################
export enum PresetIdentifierEnum {
	BUSINESS_PREMISES = 'businessPremises',
	CONTRACT = 'contract',
	ERS = 'ers',
	FCC_OPEN_TRANSACTION = 'fccOpenTransaction',
	FCC_VERSION = 'fccVersion',
	FISKAL_TSS_LOG = 'fiskalTssLog',
	KEYWORD = 'keyword',
	NOTIFICATION = 'notification',
	OPEN_ARCHIVE_EXPORT = 'openArchiveExport',
	OPEN_ARCHIVE = 'openArchive',
	TAXATION_REPORTING = 'taxationReporting',
	TSS = 'tss',
	USAGE = 'usage',
	USER = 'user'
}


// INTERFACES #########################################################
interface IPreset {
	[ PresetIdentifierEnum.BUSINESS_PREMISES ]: BusinessPremisesPresetEnum;
	[ PresetIdentifierEnum.CONTRACT ]: ContractPresetEnum;
	[ PresetIdentifierEnum.ERS ]: ErsPresetEnum;
	[ PresetIdentifierEnum.FCC_OPEN_TRANSACTION ]: FccOpenTransactionPresetEnum;
	[ PresetIdentifierEnum.FCC_VERSION ]: FccVersionPresetEnum;
	[ PresetIdentifierEnum.FISKAL_TSS_LOG ]: FiskalTssLogPresetEnum;
	[ PresetIdentifierEnum.KEYWORD ]: KeywordPresetEnum;
	[ PresetIdentifierEnum.NOTIFICATION ]: NotificationPresetEnum;
	[ PresetIdentifierEnum.OPEN_ARCHIVE_EXPORT ]: OpenArchiveExportPresetEnum;
	[ PresetIdentifierEnum.OPEN_ARCHIVE ]: OpenArchivePresetEnum;
	[ PresetIdentifierEnum.TAXATION_REPORTING ]: TaxationReportingPresetEnum;
	[ PresetIdentifierEnum.TSS ]: TssPresetEnum;
	[ PresetIdentifierEnum.USAGE ]: UsagePresetEnum;
	[ PresetIdentifierEnum.USER ]: UserPresetEnum;
}


// TYPES ##############################################################
export type IEntityVariantPresetRelation<PresetIdentifier extends PresetIdentifierEnum> = {
	[K in EntityVariantEnum]?: PresetRelationItem<PresetIdentifier>;
};

export type IPresetRelation<Preset extends PresetType, PresetIdentifier extends PresetIdentifierEnum> = {
	[K in Preset]?: PresetRelationItem<PresetIdentifier>;
};

type PresetRelationItem<PresetIdentifier extends PresetIdentifierEnum> = {
	[key in Exclude<keyof IPreset, PresetIdentifier>]?: IPreset[key]
};


export type PresetType =
	BusinessPremisesPresetEnum
	| ContractPresetEnum
	| ErsPresetEnum
	| FccOpenTransactionPresetEnum
	| FccVersionPresetEnum
	| FiskalTssLogPresetEnum
	| KeywordPresetEnum
	| NotificationPresetEnum
	| OpenArchiveExportPresetEnum
	| OpenArchivePresetEnum
	| TaxationReportingPresetEnum
	| TssPresetEnum
	| UsagePresetEnum
	| UserPresetEnum;

// MODELS #############################################################
class PresetBaseConfig {
	// base
	public presetId: PresetType = undefined;
	public productId: ProductEnum = undefined;
}


export class PresetContentConfig {
	public isDisabled?: boolean = undefined;
	public isHidden?: boolean = undefined;
	public isHoverable?: boolean = undefined;
	public isRendered?: boolean = undefined;
}

export class PresetFormContentConfig extends PresetContentConfig {
	public validatorsMap?: FormValidatorsConfigMap = new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>();
}

export class PresetActionConfig extends PresetContentConfig {
	public actionEventId: EntityActionEventEnum = undefined;
	public emitAction?: boolean = undefined;
	public isActionAllowed?: boolean = undefined;
	public scopeAction?: ScopeActionEnum = undefined;
}

export class PresetManageTenantActionConfig extends PresetActionConfig {
	public id: ManageTenantIdentifierEnum;
}


export class PresetNavigateBackConfig {
	public fallbackRoute?: IRouteDefinition = undefined;
	public listOperationsId?: ListOperationsIdentifierEnum = undefined;
}

export class PresetEntityBaseConfig extends PresetBaseConfig {
	// contents
	public showEntityActions?: boolean = undefined;
	public listOperationsId?: ListOperationsIdentifierEnum = undefined;

	// navigation
	public navigateBack?: PresetNavigateBackConfig = undefined;

	// store
	public subscribeToStore?: boolean = undefined;
}
